import React, { useState, useEffect } from 'react';
// import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Plus, Trash2, Save, Clock, AlertCircle,
  Target, Settings, Calendar, FileText, Tag, Edit,
  Smartphone, CheckCircle2, XCircle, Info,
  RefreshCw, Eye, Wifi, DollarSign, Gift
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { Client } from '@/types/client';
import { EmailTemplate } from '@/types/email';
import { emailService } from '@/services/emailService';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { apiService } from '@/lib/api';

/** Converte input numérico preservando 0 (parseInt(x)||undefined descarta 0) */
const parseOptionalInt = (s: string): number | undefined => {
  if (s === '') return undefined;
  const n = parseInt(s, 10);
  return isNaN(n) ? undefined : n;
};

// ============================================================================
// INTERFACES - NOVO SISTEMA
// ============================================================================

interface WhatsappConnection {
  id: number;
  name: string;
  connection_type?: 'oficial' | 'extra_oficial';
  phone_id: string | null;
  is_active: boolean;
  connection_status?: string;
  // Campos opcionais para compatibilidade
  has_templates?: boolean;
  template_count?: number;
  status_info?: {
    connection_status?: string;
    can_use_templates: boolean;
  };
}

interface MetaTemplate {
  id: string;
  name: string;
  category: string;
  language: string;
  status: string;
  components?: any[];
}

/** Template da API ÁtomosChat (Envios V2). */
interface AtomosChatTemplateItem {
  id: string;
  name: string;
  text?: string;
  status: string;
  categoryName?: string;
  fileType?: string;
}

interface TemplateStrategy {
  id?: number;
  client_id: number;
  connection_id?: number | null;
  template_name: string;
  strategy_type: 'LEMBRETE' | 'COBRANCA' | 'REVISTORIA' | 'REATIVACAO' | 'MARKETING' | 'PAGAMENTO' | 'ANIVERSARIO' | 'BOAS_VINDAS' | 'RENOVACAO';
  tag_code: string;
  days_before_due?: number;
  days_before_due_from?: number;
  days_before_due_to?: number;
  days_after_due?: number;
  days_after_due_from?: number;
  days_after_due_to?: number;
  is_active: boolean;
  description?: string;
  use_common_template?: boolean; // Se true, usa template comum (sem PIX/PDF) mesmo para LEMBRETE/COBRANCA
  header_enabled?: boolean; // Se true, inclui header de mídia no template comum
  header_type?: 'image' | 'video' | null; // Tipo do header: image ou video
  header_url?: string | null; // URL da mídia do header
  document_header_in_template?: boolean; // Se false, não envia header document (PDF) no template especial. Padrão true.
  common_template_document_header?: boolean; // Se true e use_common_template=true, envia PDF do boleto (link_boleto) no header do template comum.
  enviar_link_pdf_completo?: boolean; // Se true, envia URL direta do PDF (espelhado) no header document.
  atomos_template_id?: string | null;
  atomos_file_type?: string | null;
  atomos_file_column?: string | null;
  atomos_department_id?: string | null;
  atomos_department_name?: string | null;
  use_message_flow?: boolean; // Se true, usa fluxo personalizado
  message_flow_id?: number; // ID do fluxo de mensagens
  email_template_id?: number | null; // Template de email associado (null = sem email)
  auto_enviar_email?: boolean; // Auto-envio do email espelhado ao concluir o lote
  created_at?: string;
  updated_at?: string;
  tag_description?: string;
  connection?: {
    id: number;
    name: string;
    phone_id: string;
    is_active: boolean;
    connection_status?: string;
    connection_type?: 'oficial' | 'extra_oficial';
  };
}

/** Valor do Select para a "conexão" ÁtomosChat (Envios V2). */
const CONNECTION_VALUE_ATOMOS_V2 = 'atomos-v2';

interface TemplateStrategiesTabProps {
  client: Client | null;
}

// ============================================================================
// CONSTANTES
// ============================================================================

const STRATEGY_TYPES = [
  { 
    value: 'LEMBRETE', 
    label: 'Lembrete', 
    icon: Clock, 
    description: 'Mensagens antes do vencimento',
    color: 'blue'
  },
  { 
    value: 'COBRANCA', 
    label: 'Cobrança', 
    icon: AlertCircle, 
    description: 'Mensagens após vencimento',
    color: 'orange'
  },
  { 
    value: 'REVISTORIA', 
    label: 'Revistoria', 
    icon: Target, 
    description: 'Templates comuns baseados em tags',
    color: 'purple'
  },
  { 
    value: 'REATIVACAO', 
    label: 'Reativação', 
    icon: RefreshCw, 
    description: 'Tags personalizadas para reativação',
    color: 'green'
  },
  { 
    value: 'MARKETING', 
    label: 'Marketing', 
    icon: Tag, 
    description: 'Tags personalizadas para marketing',
    color: 'pink'
  },
  { 
    value: 'PAGAMENTO', 
    label: 'Pagamento', 
    icon: DollarSign, 
    description: 'Confirmação de pagamento / recibo',
    color: 'emerald'
  },
  {
    value: 'ANIVERSARIO',
    label: 'Aniversário',
    icon: Gift,
    description: 'Mensagens de parabéns aos aniversariantes',
    color: 'rose'
  },
  {
    value: 'BOAS_VINDAS',
    label: 'Boas-vindas',
    icon: Gift,
    description: 'Mensagens de boas-vindas a novos associados (apenas vincula tag ao template)',
    color: 'amber'
  },
  {
    value: 'RENOVACAO',
    label: 'Renovação',
    icon: Gift,
    description: 'Mensagens de renovação (régua Hinova por código de situação)',
    color: 'cyan'
  }
] as const;

// Tags fixas removidas - apenas tags personalizadas serão utilizadas
// As tags agora são carregadas dinamicamente do banco de dados

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const TemplateStrategiesTab: React.FC<TemplateStrategiesTabProps> = ({ client }) => {
  // Estados principais - sempre declarados no topo
  const [connections, setConnections] = useState<WhatsappConnection[]>([]);
  const [strategies, setStrategies] = useState<TemplateStrategy[]>([]);
  const [templates, setTemplates] = useState<MetaTemplate[]>([]);
  const [customTags, setCustomTags] = useState<any[]>([]);
  const [messageFlows, setMessageFlows] = useState<any[]>([]); // Novos fluxos de mensagens
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]); // Templates de email do cliente
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState<TemplateStrategy | null>(null);

  // Estados do formulário
  const [selectedConnection, setSelectedConnection] = useState<WhatsappConnection | { id: string; name: string } | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  // ÁtomosChat (Envios V2): múltiplas configs por cliente + preferência V1/V2
  const [atomosChatConfigs, setAtomosChatConfigs] = useState<{ id: number; account_name: string; channel_name?: string | null; channel_phone?: string | null; is_active?: boolean }[]>([]);
  const [envioVersionPref, setEnvioVersionPref] = useState<'v1' | 'v2'>('v1');
  const activeAtomosChatConfigs = atomosChatConfigs.filter((c) => c.is_active !== false);
  const atomosChatConfig = (envioVersionPref === 'v2' && activeAtomosChatConfigs.length > 0)
    ? { envioVersion: 'v2' as const, config: activeAtomosChatConfigs[0] }
    : null;
  const [atomosTemplates, setAtomosTemplates] = useState<AtomosChatTemplateItem[]>([]);
  const [loadingAtomosTemplates, setLoadingAtomosTemplates] = useState(false);
  const [atomosDepartments, setAtomosDepartments] = useState<{ id: string; name: string }[]>([]);
  const [loadingAtomosDepartments, setLoadingAtomosDepartments] = useState(false);
  const [formData, setFormData] = useState<Partial<TemplateStrategy>>({
    strategy_type: 'LEMBRETE',
    tag_code: 'L1',
    days_before_due: undefined,
    days_before_due_from: 3,
    days_before_due_to: 7,
    days_after_due: undefined,
    days_after_due_from: undefined,
    days_after_due_to: undefined,
    is_active: true,
    use_common_template: false,
    header_enabled: false,
    header_type: null,
    header_url: null,
    document_header_in_template: true,
    common_template_document_header: false,
    enviar_link_pdf_completo: false,
    use_message_flow: false,
    message_flow_id: undefined,
    email_template_id: null,
    auto_enviar_email: false,
  });

  // ============================================================================
  // EFEITOS
  // ============================================================================

  useEffect(() => {
    if (client?.id) {
      console.log('🔄 [TemplateStrategiesTab] Cliente alterado, carregando dados para client ID:', client.id);
      loadConnections();
      loadStrategies();
      loadCustomTags();
      loadMessageFlows();
      if (client.email_canal_enabled) {
        loadEmailTemplates(client.id);
      } else {
        setEmailTemplates([]);
      }
    }
  }, [client?.id, client?.email_canal_enabled]);

  // Log de debug para conexões carregadas
  useEffect(() => {
    console.log('📋 [TemplateStrategiesTab] Conexões carregadas:', connections);
    console.log('📋 [TemplateStrategiesTab] Conexões extra_oficial:', 
      connections.filter(c => c.connection_type === 'extra_oficial')
    );
  }, [connections]);

  // ============================================================================
  // FUNÇÕES DE CARREGAMENTO
  // ============================================================================

  const loadMessageFlows = async () => {
    if (!client?.id) return;
    
    try {
      console.log('🔄 [TemplateStrategiesTab] Carregando fluxos de mensagens para cliente:', client.id);
      const response = await apiService.get(`/message-flows/client/${client.id}`);
      
      if (response.success && response.data) {
        setMessageFlows(response.data);
        console.log('✅ [TemplateStrategiesTab] Fluxos carregados:', response.data.length);
      } else {
        setMessageFlows([]);
      }
    } catch (error) {
      console.error('❌ [TemplateStrategiesTab] Erro ao carregar fluxos:', error);
      setMessageFlows([]);
    }
  };

  const loadEmailTemplates = async (clientId: number) => {
    try {
      const list = await emailService.listTemplates(clientId);
      setEmailTemplates(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('❌ [TemplateStrategiesTab] Erro ao carregar templates de email:', error);
      setEmailTemplates([]);
    }
  };

  const loadCustomTags = async () => {
    try {
      console.log('🔄 [TemplateStrategiesTab] Carregando tags para cliente:', client!.id);
      const response = await apiService.get(`/tags/client/${client!.id}`);
      
      console.log('🔍 [TemplateStrategiesTab] Resposta da API de tags:', response);
      
      if ((response as any).success) {
        const tagsData = (response as any).data || [];
        console.log('🔍 [TemplateStrategiesTab] Tags recebidas da API:', tagsData);
        setCustomTags(tagsData);
        console.log('✅ [TemplateStrategiesTab] Tags carregadas no estado:', tagsData.length, 'tags');
        console.log('🔍 [TemplateStrategiesTab] Estado customTags atualizado:', tagsData);
        
        // Testar getTagOptions imediatamente após carregar
        setTimeout(() => {
          console.log('🧪 [TemplateStrategiesTab] Testando getTagOptions para LEMBRETE...');
          const lembreteTags = getTagOptions('LEMBRETE');
          console.log('🧪 [TemplateStrategiesTab] Tags LEMBRETE encontradas:', lembreteTags);
          
          console.log('🧪 [TemplateStrategiesTab] Testando getTagOptions para COBRANCA...');
          const cobrancaTags = getTagOptions('COBRANCA');
          console.log('🧪 [TemplateStrategiesTab] Tags COBRANCA encontradas:', cobrancaTags);
        }, 100);
      } else {
        console.warn('⚠️ [TemplateStrategiesTab] Erro ao carregar tags:', (response as any).error);
        setCustomTags([]);
      }
    } catch (error) {
      console.error('❌ [TemplateStrategiesTab] Erro ao carregar tags:', error);
      setCustomTags([]);
    }
  };

  const loadConnections = async () => {
    setLoading(true);
    const extract = (payload: any): WhatsappConnection[] => {
      if (!payload) return [];
      if ((payload as any).success && Array.isArray((payload as any).data)) return (payload as any).data as WhatsappConnection[];
      if (Array.isArray((payload as any).connections)) return (payload as any).connections as WhatsappConnection[];
      if ((payload as any).data && Array.isArray((payload as any).data.connections)) return (payload as any).data.connections as WhatsappConnection[];
      if (Array.isArray(payload)) return payload as WhatsappConnection[];
      return [] as WhatsappConnection[];
    };

    try {
      // Carregar conexões WhatsApp + lista de configs V2 + preferência V1/V2 em paralelo
      const [response, configsRes, versionRes] = await Promise.all([
        apiService.get(`/strategy-configurations/connections/${client!.id}`).catch((err: any) => {
          console.error('❌ [TemplateStrategiesTab] Erro ao carregar conexões WhatsApp:', err);
          return null;
        }),
        apiService.get(`/atomos-chat/client/${client!.id}/configs`).catch(() => null) as Promise<{ success?: boolean; configs?: any[] } | null>,
        apiService.get(`/atomos-chat/client/${client!.id}/envio-version`).catch(() => null) as Promise<{ success?: boolean; envio_version?: 'v1' | 'v2' } | null>,
      ]);

      let list = extract(response);
      if (list.length === 0 && response) {
        console.warn('⚠️ [TemplateStrategiesTab] Nenhuma conexão via strategy-configurations. Tentando /whatsapp/client/{id}');
        try {
          const fb1 = await apiService.get(`/whatsapp/client/${client!.id}`);
          list = extract(fb1);
        } catch { /* fallback silencioso */ }
      }
      if (list.length === 0 && response) {
        console.warn('⚠️ [TemplateStrategiesTab] Nenhuma conexão via clientId. Tentando /whatsapp/client/current');
        try {
          const fb2 = await apiService.get(`/whatsapp/client/current`);
          list = extract(fb2);
        } catch { /* fallback silencioso */ }
      }

      setConnections(list);

      const v2Configs = Array.isArray(configsRes?.configs) ? configsRes!.configs! : [];
      setAtomosChatConfigs(v2Configs as any);
      setEnvioVersionPref((versionRes?.envio_version === 'v2' ? 'v2' : 'v1'));
      const isV2Active = versionRes?.envio_version === 'v2' && v2Configs.length > 0;
      console.log('✅ [TemplateStrategiesTab] Conexões carregadas:', list.length, 'AtomosChat V2:', isV2Active, 'configs:', v2Configs.length);

      if (list.length === 0 && !isV2Active) {
        toast({
          title: "Aviso",
          description: "Nenhuma conexão WhatsApp ou AtomosChat V2 encontrada",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('❌ [TemplateStrategiesTab] Erro geral ao carregar conexões:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar conexões",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAtomosTemplates = async (configIdOverride?: number | null) => {
    const clientId = client?.id;
    if (!clientId) return;
    try {
      setLoadingAtomosTemplates(true);
      const cfgId = configIdOverride ?? (formData as any).atomos_chat_config_id ?? (atomosChatConfigs[0]?.id ?? null);
      const url = cfgId
        ? `/atomos-chat/client/${clientId}/templates?config_id=${cfgId}`
        : `/atomos-chat/client/${clientId}/templates`;
      const data = await apiService.get(url) as { success?: boolean; templates?: AtomosChatTemplateItem[]; data?: AtomosChatTemplateItem[] | { templates?: AtomosChatTemplateItem[] }; count?: number };
      // Aceitar templates no topo ou em data (alguns endpoints envolvem em data)
      const list = Array.isArray(data.templates)
        ? data.templates
        : Array.isArray(data.data)
          ? data.data
          : (data.data && Array.isArray((data.data as any).templates))
            ? (data.data as any).templates
            : [];
      setAtomosTemplates(list);
      if (list.length > 0) {
        toast({
          title: "Templates carregados",
          description: `${list.length} template(s) da ÁtomosChat (Envios V2).`,
        });
      }
    } catch (e: any) {
      console.error('[TemplateStrategiesTab] loadAtomosTemplates:', e);
      toast({
        title: "Erro ao carregar templates",
        description: e?.response?.data?.error ?? e?.message ?? "Verifique o token na aba API de Envio (Envios V2).",
        variant: "destructive"
      });
      setAtomosTemplates([]);
    } finally {
      setLoadingAtomosTemplates(false);
    }
  };

  const loadAtomosDepartments = async (configIdOverride?: number | null) => {
    const clientId = client?.id;
    if (!clientId) return;
    setAtomosDepartments([]);
    try {
      setLoadingAtomosDepartments(true);
      const cfgId = configIdOverride ?? (formData as any).atomos_chat_config_id ?? (atomosChatConfigs[0]?.id ?? null);
      const url = cfgId
        ? `/atomos-chat/client/${clientId}/departments?config_id=${cfgId}`
        : `/atomos-chat/client/${clientId}/departments`;
      const data = await apiService.get(url) as {
        success?: boolean;
        departments?: { id: string; name: string }[];
      };
      const list = Array.isArray(data?.departments) ? data.departments : [];
      setAtomosDepartments(list);
    } catch (e: any) {
      console.error('[TemplateStrategiesTab] loadAtomosDepartments:', e);
      setAtomosDepartments([]);
      toast({
        title: 'Erro ao carregar equipes',
        description: e?.response?.data?.error ?? e?.message ?? 'Verifique o token na aba API de Envio (Envios V2).',
        variant: 'destructive',
      });
    } finally {
      setLoadingAtomosDepartments(false);
    }
  };

  const loadStrategies = async () => {
    try {
      // Carregar estratégias (não tags)
      const response = await apiService.get(`/strategy-configurations/client/${client!.id}`);
      
      if ((response as any).success) {
        const strategiesData = (response as any).data || [];
        setStrategies(strategiesData);
        console.log('✅ [TemplateStrategiesTab] Estratégias carregadas:', strategiesData.length);
      } else {
        throw new Error((response as any).error || 'Erro ao carregar estratégias');
      }
    } catch (error) {
      console.error('❌ [TemplateStrategiesTab] Erro ao carregar estratégias:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar estratégias de templates",
        variant: "destructive"
      });
    }
  };

  const loadTemplatesForConnection = async (connectionId: number) => {
    console.log('🚀 [loadTemplatesForConnection] INICIANDO - connectionId:', connectionId);
    try {
      setLoadingTemplates(true);
      console.log('🔄 [TemplateStrategiesTab] Carregando templates para conexão:', connectionId);
      console.log('🍪 [TemplateStrategiesTab] Cookies disponíveis:', document.cookie);
      console.log('🔍 [TemplateStrategiesTab] URL da requisição:', `/strategy-configurations/templates/${connectionId}`);
      
      console.log('📡 [TemplateStrategiesTab] Fazendo chamada para API...');
      const response = await apiService.get(`/strategy-configurations/templates/${connectionId}`);
      
      console.log('📡 [TemplateStrategiesTab] API RESPONDEU!');
      console.log('🔍 [TemplateStrategiesTab] Response recebida:', response);
      console.log('🔍 [TemplateStrategiesTab] Response.success:', (response as any).success);
      console.log('🔍 [TemplateStrategiesTab] Response.data:', (response as any).data);
      
      if ((response as any).success) {
        // Garantir que sempre seja um array
        const templatesArray = Array.isArray((response as any).data) ? (response as any).data : [];
        console.log('🔍 [TemplateStrategiesTab] Response completa:', response);
        console.log('🔍 [TemplateStrategiesTab] Response.data:', (response as any).data);
        console.log('🔍 [TemplateStrategiesTab] Response.data é array?', Array.isArray((response as any).data));
        console.log('🔍 [TemplateStrategiesTab] TemplatesArray:', templatesArray);
        
        console.log('💾 [TemplateStrategiesTab] Salvando templates no state...');
        setTemplates(templatesArray);
        console.log('✅ [TemplateStrategiesTab] Templates carregados:', templatesArray.length);
        
        if (templatesArray.length === 0) {
          toast({
            title: "Informação",
            description: "Nenhum template encontrado para esta conexão",
            variant: "default"
          });
        }
      } else {
        console.error('❌ [TemplateStrategiesTab] Response não foi bem-sucedida:', response);
        throw new Error((response as any).error || 'Erro ao carregar templates');
      }
    } catch (error) {
      console.error('❌ [TemplateStrategiesTab] Erro ao carregar templates:', error);
      console.error('❌ [TemplateStrategiesTab] Detalhes do erro:', {
        message: (error as any)?.message,
        stack: (error as any)?.stack,
        status: (error as any)?.status,
        response: (error as any)?.response
      });
      toast({
        title: "Erro",
        description: "Erro ao carregar templates da Meta",
        variant: "destructive"
      });
      setTemplates([]);
    } finally {
      console.log('🏁 [TemplateStrategiesTab] Finalizando carregamento...');
      setLoadingTemplates(false);
    }
  };

  // ============================================================================
  // FUNÇÕES DO FORMULÁRIO
  // ============================================================================

  const handleConnectionChange = (connectionId: string) => {
    if (connectionId === CONNECTION_VALUE_ATOMOS_V2) {
      setSelectedConnection({ id: CONNECTION_VALUE_ATOMOS_V2, name: 'ÁtomosChat (Envios V2)' });
      setFormData(prev => ({
        ...prev,
        connection_id: null,
        template_name: undefined,
        atomos_template_id: undefined,
        atomos_file_type: undefined,
        atomos_file_column: 'link_boleto',
        atomos_department_id: undefined,
        atomos_department_name: undefined,
        use_message_flow: false,
        message_flow_id: undefined
      }));
      setTemplates([]);
      loadAtomosTemplates();
      loadAtomosDepartments();
      return;
    }

    const connection = connections.find(c => c.id === parseInt(connectionId));
    console.log('🔍 [handleConnectionChange] Conexão selecionada:', connection);
    console.log('🔍 [handleConnectionChange] Tipo:', connection?.connection_type);
    console.log('🔍 [handleConnectionChange] Todas conexões disponíveis:', connections);
    
    setSelectedConnection(connection || null);
    setFormData(prev => ({
      ...prev,
      connection_id: parseInt(connectionId),
      template_name: undefined,
      atomos_template_id: undefined,
      atomos_file_type: undefined,
      atomos_file_column: undefined,
      atomos_department_id: undefined,
      atomos_department_name: undefined,
      use_message_flow: connection?.connection_type === 'extra_oficial', // Auto-ativar para extra_oficial
      message_flow_id: undefined
    }));
    setAtomosTemplates([]);

    console.log('🔍 [handleConnectionChange] Conexão selecionada:', connection);
    console.log('🔍 [handleConnectionChange] Tipo:', connection?.connection_type);

    // Apenas carregar templates se NÃO for extra_oficial
    if (connection && connection.connection_type !== 'extra_oficial' && canUseTemplates(connection)) {
      console.log('✅ [handleConnectionChange] Carregando templates para conexão:', connectionId);
      loadTemplatesForConnection(parseInt(connectionId));
    } else {
      console.log('❌ [handleConnectionChange] Não carrega templates (extra_oficial ou sem permissão)');
      setTemplates([]);
    }
  };

  const handleStrategyTypeChange = (strategyType: string) => {
    let firstTag: string;
    let daysBefore: number | undefined;
    let daysAfter: number | undefined;
    
    if (strategyType === 'LEMBRETE') {
      firstTag = 'L1';
      daysBefore = undefined;
      daysAfter = undefined;
    } else if (strategyType === 'COBRANCA') {
      firstTag = 'M1';
      daysBefore = undefined;
      daysAfter = 3;
    } else if (strategyType === 'REVISTORIA') {
      firstTag = 'R1';
      daysBefore = undefined;
      daysAfter = 3;
    } else if (strategyType === 'ANIVERSARIO') {
      firstTag = 'aniversario-1';
      daysBefore = undefined;
      daysAfter = undefined;
    } else if (strategyType === 'MARKETING' || strategyType === 'REATIVACAO' || strategyType === 'PAGAMENTO') {
      firstTag = 'M1';
      daysBefore = undefined;
      daysAfter = undefined;
    } else {
      firstTag = 'L1';
      daysBefore = undefined;
      daysAfter = undefined;
    }
    
    setFormData(prev => ({ 
      ...prev, 
      strategy_type: strategyType as any,
      tag_code: firstTag,
      days_before_due: daysBefore,
      days_before_due_from: strategyType === 'LEMBRETE' ? 3 : undefined,
      days_before_due_to: strategyType === 'LEMBRETE' ? 7 : undefined,
      days_after_due: daysAfter,
      days_after_due_from: (strategyType === 'COBRANCA' || strategyType === 'REVISTORIA') ? daysAfter : undefined,
      days_after_due_to: (strategyType === 'COBRANCA' || strategyType === 'REVISTORIA') ? daysAfter : undefined
    }));
  };

  const isV2Strategy = formData.connection_id == null && atomosChatConfig != null;

  // Auto-selecionar a única conexão V2 quando o cliente tem só uma
  useEffect(() => {
    if (!isV2Strategy) return;
    const cur = (formData as any).atomos_chat_config_id;
    if (cur != null) return;
    if (activeAtomosChatConfigs.length === 1) {
      setFormData(prev => ({ ...(prev as any), atomos_chat_config_id: activeAtomosChatConfigs[0].id } as any));
    }
  }, [isV2Strategy, activeAtomosChatConfigs]);

  // Quando o formulário abre já em modo V2 (conexão ÁtomosChat auto-selecionada,
  // sem passar pelo handleConnectionChange), carrega templates E equipes do config
  // resolvido. Sem isto, o dropdown "Equipe" fica vazio: diferente dos templates,
  // ele não tem botão "Carregar", então só seria preenchido se o usuário trocasse
  // manualmente a conexão. Espelha o que o handleConnectionChange faz para V2.
  useEffect(() => {
    if (!showForm || !isV2Strategy) return;
    const cfgId = (formData as any).atomos_chat_config_id ?? activeAtomosChatConfigs[0]?.id ?? null;
    if (!cfgId) return;
    loadAtomosDepartments(cfgId);
    if (atomosTemplates.length === 0) loadAtomosTemplates(cfgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, isV2Strategy, (formData as any).atomos_chat_config_id, activeAtomosChatConfigs.length]);

  const handleSubmit = async () => {
    // Fonte da verdade: conexão extra_oficial = fluxo personalizado (não exige template_name)
    const isExtraOficialFlow = !!(formData.use_message_flow || selectedConnection?.connection_type === 'extra_oficial');
    console.log('🔄 [TemplateStrategiesTab] Iniciando salvamento da estratégia');
    console.log('🔍 [TemplateStrategiesTab] formData:', formData);
    console.log('🔍 [TemplateStrategiesTab] selectedConnection:', selectedConnection);
    console.log('🔍 [TemplateStrategiesTab] isExtraOficialFlow:', isExtraOficialFlow);
    console.log('🔍 [TemplateStrategiesTab] message_flow_id:', formData.message_flow_id);
    
    // Validações: V2 exige template_name + atomos_template_id; Meta exige connection_id + template_name; Extra oficial exige connection_id + message_flow_id
    if (isV2Strategy) {
      if (!formData.template_name || !formData.atomos_template_id || !formData.strategy_type || !formData.tag_code) {
        toast({
          title: "Erro",
          description: "Preencha todos os campos obrigatórios (Conexão, Template, Tipo, Tag)",
          variant: "destructive"
        });
        return;
      }
    } else if (isExtraOficialFlow) {
      // Conexão extra_oficial: obrigatório connection_id, message_flow_id, strategy_type, tag_code
      if (!formData.connection_id || !formData.message_flow_id || !formData.strategy_type || !formData.tag_code) {
        toast({
          title: "Erro",
          description: "Preencha todos os campos obrigatórios (Conexão, Fluxo de Mensagens, Tipo, Tag)",
          variant: "destructive"
        });
        return;
      }
    } else {
      // Conexões Meta API: validar template_name
      if (!formData.connection_id || !formData.template_name || !formData.strategy_type || !formData.tag_code) {
        toast({
          title: "Erro",
          description: "Preencha todos os campos obrigatórios",
          variant: "destructive"
        });
        return;
      }
    }

    // Se a estratégia V2 tem department_id, garantir que ainda existe na lista atual
    if (
      formData.connection_id == null &&
      formData.atomos_department_id &&
      atomosDepartments.length > 0 &&
      !atomosDepartments.some(d => d.id === formData.atomos_department_id)
    ) {
      toast({
        title: 'Equipe inválida',
        description: 'A equipe selecionada não existe mais no ÁtomosChat. Selecione outra.',
        variant: 'destructive',
      });
      return;
    }

    // Validação específica para campos de dias
    const hasPeriodLembrete = formData.days_before_due_from != null && formData.days_before_due_to != null;
    const hasLegacyLembrete = formData.days_before_due != null && formData.days_before_due >= 0;
    if (formData.strategy_type === 'LEMBRETE' && !hasPeriodLembrete && !hasLegacyLembrete) {
      toast({
        title: "Erro",
        description: "Para estratégias de LEMBRETE, informe o período (De e Até dias antes do vencimento)",
        variant: "destructive"
      });
      return;
    }
    if (formData.strategy_type === 'LEMBRETE' && hasPeriodLembrete) {
      const from = formData.days_before_due_from!;
      const to = formData.days_before_due_to!;
      if (from < 0 || to < 0) {
        toast({
          title: "Erro",
          description: "Os valores De e Até devem ser >= 0",
          variant: "destructive"
        });
        return;
      }
      if (from > to) {
        toast({
          title: "Erro",
          description: "O valor 'De' deve ser menor ou igual ao valor 'Até'",
          variant: "destructive"
        });
        return;
      }
    }

    const hasPeriodCobranca = (formData.days_after_due_from != null && formData.days_after_due_to != null) || formData.days_after_due != null;
    if ((formData.strategy_type === 'COBRANCA' || formData.strategy_type === 'REVISTORIA') && !hasPeriodCobranca) {
      toast({
        title: "Erro",
        description: `Para estratégias de ${formData.strategy_type}, informe o período (de e até dias após o vencimento)`,
        variant: "destructive"
      });
      return;
    }
    const from = formData.days_after_due_from ?? formData.days_after_due;
    const to = formData.days_after_due_to ?? formData.days_after_due;
    if ((formData.strategy_type === 'COBRANCA' || formData.strategy_type === 'REVISTORIA' || formData.strategy_type === 'REATIVACAO') && from != null && to != null && from > to) {
      toast({
        title: "Erro",
        description: "O valor 'De' deve ser menor ou igual ao valor 'Até'",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      
      // Verificar se o client está definido
      if (!client || !client.id) {
        toast({
          title: "Erro",
          description: "Cliente não encontrado. Recarregue a página.",
          variant: "destructive"
        });
        return;
      }

      console.log('🔍 [TemplateStrategiesTab] Client:', client);
      console.log('🔍 [TemplateStrategiesTab] Client ID:', client.id);
      console.log('🔍 [TemplateStrategiesTab] editingStrategy:', editingStrategy);

      if (editingStrategy) {
        // Atualizar estratégia completa (Meta ou V2) – normalizar dias para evitar rejeição do backend
        const daysBefore = formData.strategy_type === 'LEMBRETE' && (formData.days_before_due_from == null || formData.days_before_due_to == null) ? (formData.days_before_due ?? 0) : undefined;
        const daysBeforeFrom = formData.strategy_type === 'LEMBRETE' ? (formData.days_before_due_from ?? formData.days_before_due) : undefined;
        const daysBeforeTo = formData.strategy_type === 'LEMBRETE' ? (formData.days_before_due_to ?? formData.days_before_due) : undefined;
        const daysFrom = (formData.strategy_type === 'COBRANCA' || formData.strategy_type === 'REVISTORIA' || formData.strategy_type === 'REATIVACAO') ? (formData.days_after_due_from ?? formData.days_after_due) : undefined;
        const daysTo = (formData.strategy_type === 'COBRANCA' || formData.strategy_type === 'REVISTORIA' || formData.strategy_type === 'REATIVACAO') ? (formData.days_after_due_to ?? formData.days_after_due) : undefined;
        const isExtraFlow = !!(formData.use_message_flow || selectedConnection?.connection_type === 'extra_oficial');
        const strategyData: any = {
          client_id: client.id,
          connection_id: isV2Strategy ? null : formData.connection_id,
          template_name: isExtraFlow ? null : formData.template_name,
          strategy_type: formData.strategy_type,
          tag_code: formData.tag_code,
          days_before_due: daysBefore,
          days_before_due_from: daysBeforeFrom,
          days_before_due_to: daysBeforeTo,
          days_after_due_from: daysFrom,
          days_after_due_to: daysTo,
          is_active: formData.is_active,
          description: formData.description,
          use_common_template: formData.use_common_template === true,
          header_enabled: formData.header_enabled === true,
          header_type: formData.header_enabled ? formData.header_type : null,
          header_url: formData.header_enabled ? formData.header_url : null,
          document_header_in_template: formData.document_header_in_template !== false,
          common_template_document_header: formData.common_template_document_header === true,
          enviar_link_pdf_completo: formData.enviar_link_pdf_completo === true,
          use_message_flow: isExtraFlow,
          message_flow_id: isExtraFlow ? formData.message_flow_id : null,
          email_template_id: formData.email_template_id ?? null,
          auto_enviar_email: formData.auto_enviar_email ?? false,
        };
        if (isV2Strategy) {
          strategyData.atomos_template_id = formData.atomos_template_id ?? undefined;
          strategyData.atomos_file_type = formData.atomos_file_type ?? undefined;
          strategyData.atomos_file_column = formData.atomos_file_column ?? 'link_boleto';
          strategyData.atomos_department_id = formData.atomos_department_id ?? null;
          strategyData.atomos_department_name = formData.atomos_department_name ?? null;
          strategyData.atomos_chat_config_id = (formData as any).atomos_chat_config_id ?? null;
        }

        console.log('🔧 [TemplateStrategiesTab] Atualizando estratégia completa:', strategyData);

        await apiService.put(`/strategy-configurations/${editingStrategy.id}`, strategyData);
        toast({
          title: "Sucesso",
          description: "Estratégia atualizada com sucesso"
        });
      } else {
        // Criar nova estratégia (Meta ou V2) – normalizar dias para validação do backend
        const daysBefore = formData.strategy_type === 'LEMBRETE' && (formData.days_before_due_from == null || formData.days_before_due_to == null) ? (formData.days_before_due ?? 0) : undefined;
        const daysBeforeFrom = formData.strategy_type === 'LEMBRETE' ? (formData.days_before_due_from ?? formData.days_before_due) : undefined;
        const daysBeforeTo = formData.strategy_type === 'LEMBRETE' ? (formData.days_before_due_to ?? formData.days_before_due) : undefined;
        const daysFrom = (formData.strategy_type === 'COBRANCA' || formData.strategy_type === 'REVISTORIA' || formData.strategy_type === 'REATIVACAO') ? (formData.days_after_due_from ?? formData.days_after_due) : undefined;
        const daysTo = (formData.strategy_type === 'COBRANCA' || formData.strategy_type === 'REVISTORIA' || formData.strategy_type === 'REATIVACAO') ? (formData.days_after_due_to ?? formData.days_after_due) : undefined;
        const isExtraFlow = !!(formData.use_message_flow || selectedConnection?.connection_type === 'extra_oficial');
        const strategyData: any = {
          client_id: client.id,
          connection_id: isV2Strategy ? null : formData.connection_id,
          template_name: isExtraFlow ? null : formData.template_name,
          strategy_type: formData.strategy_type,
          tag_code: formData.tag_code,
          days_before_due: daysBefore,
          days_before_due_from: daysBeforeFrom,
          days_before_due_to: daysBeforeTo,
          days_after_due_from: daysFrom,
          days_after_due_to: daysTo,
          is_active: formData.is_active,
          description: formData.description,
          use_common_template: formData.use_common_template === true,
          header_enabled: formData.header_enabled === true,
          header_type: formData.header_enabled ? formData.header_type : null,
          header_url: formData.header_enabled ? formData.header_url : null,
          document_header_in_template: formData.document_header_in_template !== false,
          common_template_document_header: formData.common_template_document_header === true,
          enviar_link_pdf_completo: formData.enviar_link_pdf_completo === true,
          use_message_flow: isExtraFlow,
          message_flow_id: isExtraFlow ? formData.message_flow_id : null,
          email_template_id: formData.email_template_id ?? null,
          auto_enviar_email: formData.auto_enviar_email ?? false,
        };
        if (isV2Strategy) {
          strategyData.atomos_template_id = formData.atomos_template_id ?? undefined;
          strategyData.atomos_file_type = formData.atomos_file_type ?? undefined;
          strategyData.atomos_file_column = formData.atomos_file_column ?? 'link_boleto';
          strategyData.atomos_department_id = formData.atomos_department_id ?? null;
          strategyData.atomos_department_name = formData.atomos_department_name ?? null;
          strategyData.atomos_chat_config_id = (formData as any).atomos_chat_config_id ?? null;
        }

        console.log('🔧 [TemplateStrategiesTab] Criando nova estratégia:', strategyData);

        await apiService.post('/strategy-configurations', strategyData);
        toast({
          title: "Sucesso", 
          description: "Estratégia criada com sucesso"
        });
      }

      handleCancelForm();
      await loadStrategies();
      
    } catch (error: any) {
      console.error('❌ [TemplateStrategiesTab] Erro ao salvar estratégia:', error);
      
      let errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || "Erro ao salvar estratégia";
      
      // Melhorar mensagens de erro específicas
      if (errorMessage.includes("Já existe uma estratégia configurada") || errorMessage.includes("estratégia idêntica")) {
        errorMessage = "Já existe uma estratégia com mesmo tipo, tag, template e conexão. Edite a existente ou use tipo/tag/template/dias diferentes.";
      } else if (errorMessage.includes("não é válida para estratégia")) {
        errorMessage = "A tag selecionada não é compatível com o tipo de estratégia escolhido.";
      } else if (errorMessage.includes("days_before_due") || errorMessage.includes("dias antes")) {
        errorMessage = "Para Lembrete, informe os dias antes do vencimento (0 = no dia do vencimento).";
      } else if (errorMessage.includes("days_after_due") || errorMessage.includes("dias após") || errorMessage.includes("período")) {
        errorMessage = "Para Cobrança/Revistoria, informe o período (de e até dias após o vencimento).";
      }
      
      toast({
        title: "Erro ao salvar",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (strategy: TemplateStrategy) => {
    console.log('🔧 [EDIT] Iniciando edição da estratégia:', strategy);
    
    // Se já está editando a mesma estratégia, ocultar o formulário
    if (editingStrategy && editingStrategy.id === strategy.id) {
      console.log('🔧 [EDIT] Ocultando formulário...');
      setEditingStrategy(null);
      setShowForm(false);
      return;
    }
    
    setEditingStrategy(strategy);
    setFormData({
      connection_id: strategy.connection_id ?? undefined,
      template_name: strategy.template_name,
      strategy_type: strategy.strategy_type,
      tag_code: strategy.tag_code,
      days_before_due: strategy.days_before_due,
      days_before_due_from: strategy.days_before_due_from ?? strategy.days_before_due,
      days_before_due_to: strategy.days_before_due_to ?? strategy.days_before_due,
      days_after_due: strategy.days_after_due,
      days_after_due_from: strategy.days_after_due_from ?? strategy.days_after_due,
      days_after_due_to: strategy.days_after_due_to ?? strategy.days_after_due,
      is_active: strategy.is_active,
      description: strategy.description,
      use_common_template: strategy.use_common_template || false,
      header_enabled: strategy.header_enabled || false,
      header_type: strategy.header_type || null,
      header_url: strategy.header_url || null,
      document_header_in_template: strategy.document_header_in_template !== false,
      common_template_document_header: strategy.common_template_document_header || false,
      enviar_link_pdf_completo: strategy.enviar_link_pdf_completo || false,
      atomos_template_id: strategy.atomos_template_id ?? undefined,
      atomos_file_type: strategy.atomos_file_type ?? undefined,
      atomos_file_column: strategy.atomos_file_column ?? 'link_boleto',
      atomos_department_id: strategy.atomos_department_id ?? undefined,
      atomos_department_name: strategy.atomos_department_name ?? undefined,
      email_template_id: strategy.email_template_id ?? null,
      auto_enviar_email: strategy.auto_enviar_email ?? false,
      ...((strategy as any).atomos_chat_config_id != null
        ? { atomos_chat_config_id: (strategy as any).atomos_chat_config_id }
        : {}),
    } as any);

    // Estratégia V2 (connection_id null): usar "conexão" ÁtomosChat e carregar templates V2
    if (strategy.connection_id == null) {
      setSelectedConnection({ id: CONNECTION_VALUE_ATOMOS_V2, name: 'ÁtomosChat (Envios V2)' });
      setTemplates([]);
      loadAtomosTemplates();
      loadAtomosDepartments();
      setShowForm(true);
      return;
    }

    // Meta: carregar conexão e templates
    const connection = connections.find(c => c.id === strategy.connection_id);
    console.log('🔧 [EDIT] Conexão encontrada:', connection);
    console.log('🔧 [EDIT] strategy.connection_id:', strategy.connection_id);
    console.log('🔧 [EDIT] connections disponíveis:', connections.map(c => ({ id: c.id, name: c.name })));
    
    if (connection) {
      console.log('🔧 [EDIT] Definindo selectedConnection...');
      setSelectedConnection(connection);
      setAtomosTemplates([]);
      
      const canUse = canUseTemplates(connection);
      if (canUse) {
        console.log('🔧 [EDIT] Carregando templates para conexão:', connection.id);
        loadTemplatesForConnection(connection.id);
      } else {
        setTemplates([]);
      }
    } else {
      console.log('❌ [EDIT] Conexão não encontrada para ID:', strategy.connection_id);
      setTemplates([]);
    }

    setShowForm(true);
  };

  const handleDelete = async (strategyId: number) => {
    if (!confirm('Tem certeza que deseja excluir esta estratégia?')) {
      return;
    }

    try {
      await apiService.delete(`/strategy-configurations/${strategyId}`);
      toast({
        title: "Sucesso",
        description: "Estratégia excluída com sucesso"
      });
      await loadStrategies();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.response?.data?.message || "Erro ao excluir estratégia",
        variant: "destructive"
      });
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingStrategy(null);
    setSelectedConnection(null);
    setTemplates([]);
    setAtomosTemplates([]);
    setFormData({
      strategy_type: 'LEMBRETE',
      tag_code: 'L1',
      days_before_due: undefined,
      days_before_due_from: 3,
      days_before_due_to: 7,
      days_after_due: undefined,
      days_after_due_from: undefined,
      days_after_due_to: undefined,
      is_active: true,
      use_common_template: false,
      header_enabled: false,
      header_type: null,
      header_url: null,
      document_header_in_template: true,
      common_template_document_header: false,
      enviar_link_pdf_completo: false,
      connection_id: undefined,
      template_name: undefined,
      atomos_template_id: undefined,
      atomos_file_type: undefined,
      atomos_file_column: undefined,
      atomos_department_id: undefined,
      atomos_department_name: undefined,
      use_message_flow: false,
      message_flow_id: undefined,
      email_template_id: null,
      auto_enviar_email: false,
    });
  };

  // ============================================================================
  // FUNÇÕES AUXILIARES
  // ============================================================================

  // Função para determinar se uma conexão pode usar templates
  const canUseTemplates = (connection: WhatsappConnection): boolean => {
    console.log('🔍 [canUseTemplates] Verificando conexão:', {
      id: connection.id,
      name: connection.name,
      is_active: connection.is_active,
      phone_id: connection.phone_id,
      connection_type: connection.connection_type,
      status_info: connection.status_info
    });
    
    // Se a conexão tem status_info definido, usar o valor original
    if (connection.status_info?.can_use_templates !== undefined) {
      console.log('🔍 [canUseTemplates] Usando status_info.can_use_templates:', connection.status_info.can_use_templates);
      return connection.status_info.can_use_templates;
    }
    
    // Para conexões oficiais, verificar se tem phone_id
    if (connection.connection_type === 'oficial') {
      const canUse = connection.is_active && !!connection.phone_id;
      console.log('🔍 [canUseTemplates] Conexão oficial - canUse:', canUse);
      return canUse;
    }
    
    // Para conexões extra-oficiais, apenas verificar se está ativa
    const canUse = connection.is_active;
    console.log('🔍 [canUseTemplates] Conexão extra-oficial - canUse:', canUse);
    return canUse;
  };

  const getTagOptions = (strategyType: string) => {
    console.log('🔍 [getTagOptions] Buscando tags para tipo:', strategyType);
    console.log('🔍 [getTagOptions] customTags state:', customTags);
    console.log('🔍 [getTagOptions] customTags length:', customTags?.length);
    
    // Verificar se customTags é um array válido
    if (!Array.isArray(customTags) || customTags.length === 0) {
      console.log('⚠️ [getTagOptions] Nenhuma tag disponível para o tipo:', strategyType);
      console.log('⚠️ [getTagOptions] customTags não é array ou está vazio:', customTags);
      return [];
    }
    
    // Buscar tags do tipo especificado (incluindo tags inativas para mostrar ao usuário)
    const tagsForType = customTags.filter(tag => {
      const isTypeMatch = tag.type === strategyType;
      
      console.log('🔍 [getTagOptions] Tag:', tag.name, 'tipo:', tag.type, 'ativa:', tag.is_active, 'match:', isTypeMatch);
      
      return isTypeMatch;
    });
    
    console.log('🔍 [getTagOptions] Tags encontradas para', strategyType, ':', tagsForType.length);
    console.log('🔍 [getTagOptions] Tags filtradas:', tagsForType);
    
    // Converter tags para o formato esperado
    const tagOptions = tagsForType.map(tag => ({
      value: tag.tag_code || tag.id.toString(), // Usar tag_code se disponível, senão ID
      label: tag.is_active ? tag.name : `${tag.name} (INATIVA)`, // Marcar tags inativas
      description: tag.description || `${tag.name} (${strategyType})`,
      disabled: !tag.is_active // Desabilitar tags inativas
    }));
    
    console.log('🔍 [getTagOptions] Tags convertidas:', tagOptions);
    
    return tagOptions;
  };

  const getStrategyInfo = (type: string) => {
    return STRATEGY_TYPES.find(s => s.value === type) || STRATEGY_TYPES[0];
  };

  const getConnectionIcon = (connection: WhatsappConnection) => {
    if (!connection.is_active) return <XCircle className="w-4 h-4 text-red-500" />;
    if (!canUseTemplates(connection)) return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    return <CheckCircle2 className="w-4 h-4 text-green-500" />;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className="space-y-4 mt-0">
        <AtomicLoadingScreen 
          message="Carregando sistema de estratégias..." 
          className="h-64"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-600" />
            Estratégias de Templates
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Configure templates da Meta por conexão WhatsApp para diferentes estratégias de envio
          </p>
        </div>
        
        <Button 
          onClick={() => setShowForm(true)}
          className="bg-purple-600 hover:bg-purple-700"
          disabled={connections.length === 0 && atomosChatConfig?.envioVersion !== 'v2'}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Estratégia
        </Button>
      </div>

      {/* Status das Conexões */}
      {(connections.length > 0 || atomosChatConfig?.envioVersion === 'v2') && (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Smartphone className="w-5 h-5" />
              Status das Conexões
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {atomosChatConfig?.envioVersion === 'v2' && (
                <div className="flex items-center gap-3 p-3 border rounded-lg border-purple-200 bg-purple-50">
                  <CheckCircle2 className="w-4 h-4 text-purple-600" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">ÁtomosChat (Envios V2)</div>
                    <div className="text-xs text-gray-500">
                      Templates via API ÁtomosChat
                    </div>
                  </div>
                  <Badge variant="default" className="bg-purple-600">Ativo</Badge>
                </div>
              )}
              {connections
                .filter((connection) => {
                  // Conexões extra_oficial não precisam de templates
                  if (connection.connection_type === 'extra_oficial') {
                    return true;
                  }
                  // Conexões oficial precisam ter templates
                  return (connection.template_count || 0) > 0;
                })
                .map((connection) => (
                <div key={connection.id} className="flex items-center gap-3 p-3 border rounded-lg">
                  {getConnectionIcon(connection)}
                  <div className="flex-1">
                    <div className="font-medium text-sm">{connection.name}</div>
                    <div className="text-xs text-gray-500">
                      {connection.connection_type === 'oficial' ? 'Meta API' : 'Extra Oficial'} •
                      {connection.template_count} templates
                    </div>
                  </div>
                          <Badge variant={canUseTemplates(connection) ? 'default' : 'secondary'}>
          {canUseTemplates(connection) ? 'Ativo' : 'Limitado'}
        </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerta sobre tags inativas */}
      {customTags.some(tag => !tag.is_active) && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <strong>Tags Inativas Encontradas:</strong> Você tem tags personalizadas que estão inativas. 
            Elas aparecerão marcadas como "(INATIVA)" na seleção. Para usá-las, ative-as primeiro nas regras de negócio.
          </AlertDescription>
        </Alert>
      )}

      {/* Informações sobre as tags salvas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Clock className="w-5 h-5" />
              Tags de Lembrete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {customTags.filter(tag => tag.type === 'LEMBRETE' && tag.is_active).length > 0 ? (
              customTags
                .filter(tag => tag.type === 'LEMBRETE' && tag.is_active)
                .map((tag) => (
                  <div key={tag.id} className="flex items-center gap-2 text-sm">
                    <Badge variant="outline">{tag.tag_code || tag.name}</Badge>
                  </div>
                ))
            ) : (
              <div className="text-sm text-gray-500 italic">
                Nenhuma tag de lembrete configurada
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-700">
              <AlertCircle className="w-5 h-5" />
              Tags de Cobrança
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {customTags.filter(tag => tag.type === 'COBRANCA' && tag.is_active).length > 0 ? (
              customTags
                .filter(tag => tag.type === 'COBRANCA' && tag.is_active)
                .map((tag) => (
                  <div key={tag.id} className="flex items-center gap-2 text-sm">
                    <Badge variant="outline">{tag.tag_code || tag.name}</Badge>
                  </div>
                ))
            ) : (
              <div className="text-sm text-gray-500 italic">
                Nenhuma tag de cobrança configurada
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <Target className="w-5 h-5" />
              Tags de Revistoria
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {customTags.filter(tag => tag.type === 'REVISTORIA' && tag.is_active).length > 0 ? (
              customTags
                .filter(tag => tag.type === 'REVISTORIA' && tag.is_active)
                .map((tag) => (
                  <div key={tag.id} className="flex items-center gap-2 text-sm">
                    <Badge variant="outline">{tag.tag_code || tag.name}</Badge>
                  </div>
                ))
            ) : (
              <div className="text-sm text-gray-500 italic">
                Nenhuma tag de revistoria configurada
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alerta se não há conexões nem V2 */}
      {connections.length === 0 && !atomosChatConfig?.envioVersion && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Nenhuma conexão WhatsApp encontrada. Configure uma conexão na aba &quot;Conexões WhatsApp&quot; ou envios V2 (ÁtomosChat) na aba &quot;API de Envio&quot;.
          </AlertDescription>
        </Alert>
      )}

      {/* Formulário para nova estratégia */}
      {showForm && !editingStrategy && (
        <Card className="border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Nova Estratégia de Template
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Seleção de Conexão */}
            <div>
              <Label htmlFor="connection_id">Conexão WhatsApp *</Label>
              <Select
                value={formData.connection_id == null && atomosChatConfig ? CONNECTION_VALUE_ATOMOS_V2 : (formData.connection_id?.toString() ?? '')}
                onValueChange={handleConnectionChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conexão" />
                </SelectTrigger>
                <SelectContent>
                  {atomosChatConfig?.envioVersion === 'v2' && (
                    <SelectItem value={CONNECTION_VALUE_ATOMOS_V2}>
                      <div className="flex items-center gap-2">
                        <Wifi className="w-4 h-4" />
                        <div>
                          <div className="font-medium">ÁtomosChat (Envios V2)</div>
                          <div className="text-xs text-gray-500">Templates da API ÁtomosChat</div>
                        </div>
                      </div>
                    </SelectItem>
                  )}
                  {connections.map((connection) => (
                    <SelectItem
                      key={connection.id}
                      value={connection.id.toString()}
                      disabled={connection.connection_type !== 'extra_oficial' && !canUseTemplates(connection)}
                      title={connection.connection_type === 'oficial' && (connection.template_count || 0) === 0 ? 'Configure os templates na aba Conexões WhatsApp primeiro' : undefined}
                    >
                      <div className="flex items-center gap-2">
                        {getConnectionIcon(connection)}
                        <div>
                          <div className="font-medium">{connection.name}</div>
                          <div className="text-xs text-gray-500">
                            {connection.connection_type === 'oficial' ? 'Meta API' : 'Extra Oficial'} •
                            {connection.template_count} templates
                          </div>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Seletor de conexão V2 (quando >1 config V2 ativa do cliente) */}
            {isV2Strategy && activeAtomosChatConfigs.length > 1 && (
              <div>
                <Label htmlFor="atomos_chat_config_id">Conexão V2 *</Label>
                <Select
                  value={(formData as any).atomos_chat_config_id ? String((formData as any).atomos_chat_config_id) : ''}
                  onValueChange={(v) => {
                    const id = v ? Number(v) : null;
                    setFormData(prev => ({ ...(prev as any), atomos_chat_config_id: id } as any));
                    if (id) {
                      loadAtomosTemplates(id);
                      loadAtomosDepartments(id);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a conexão V2" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeAtomosChatConfigs.map((cfg) => (
                      <SelectItem key={cfg.id} value={String(cfg.id)}>
                        {cfg.account_name}{cfg.channel_phone ? ` — ${cfg.channel_phone}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Esta estratégia enviará pela conexão V2 escolhida.
                </p>
              </div>
            )}

            {/* Tipo de Envio (para conexões extra_oficial) */}
            {selectedConnection && selectedConnection.connection_type === 'extra_oficial' && (
              <Card className="border-purple-200 bg-purple-50">
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <Label className="text-base font-medium">Fluxo de Mensagens Personalizado</Label>
                        <p className="text-sm text-gray-600 mt-1">
                          Para API Extra Oficial, selecione um fluxo de mensagens configurado
                        </p>
                      </div>
                    </div>

                    <div>
                      <Label>Selecionar Fluxo de Mensagens *</Label>
                      <Select
                        value={formData.message_flow_id?.toString() ?? ''}
                        onValueChange={(value) => {
                          setFormData(prev => ({ ...prev, message_flow_id: parseInt(value) }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um fluxo" />
                        </SelectTrigger>
                        <SelectContent>
                          {messageFlows.length > 0 ? (
                            messageFlows
                              .filter(flow => flow.connection_id === selectedConnection.id && flow.is_active)
                              .map(flow => (
                                <SelectItem key={flow.id} value={flow.id!.toString()}>
                                  <div>
                                    <div className="font-medium">{flow.name}</div>
                                    {flow.description && (
                                      <div className="text-xs text-gray-500">{flow.description}</div>
                                    )}
                                  </div>
                                </SelectItem>
                              ))
                          ) : (
                            <div className="px-2 py-2 text-sm text-gray-500">
                              Nenhum fluxo disponível. Crie um na aba "Fluxos de Envio".
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                      <Alert className="mt-3">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="text-xs">
                          Configure fluxos de mensagens na aba <strong>"Fluxos de Envio"</strong>.
                          As mensagens e delays são definidos no fluxo selecionado.
                        </AlertDescription>
                      </Alert>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Seleção de Template (Meta ou ÁtomosChat V2) - Esconder se use_message_flow */}
            {!formData.use_message_flow && (
            <div>
              <Label htmlFor="template_name">{formData.connection_id == null && atomosChatConfig ? 'Template *' : 'Template da Meta *'}</Label>
              {formData.connection_id == null && atomosChatConfig ? (
                <Select
                  value={formData.atomos_template_id ?? ''}
                  onValueChange={(value) => {
                    const template = atomosTemplates.find(t => t.id === value);
                    if (template) {
                      setFormData(prev => ({
                        ...prev,
                        template_name: template.name,
                        atomos_template_id: template.id,
                        atomos_file_type: template.fileType ?? undefined,
                        atomos_file_column: 'link_boleto'
                      }));
                    }
                  }}
                  disabled={loadingAtomosTemplates}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={loadingAtomosTemplates ? "Carregando..." : "Selecione o template"} />
                  </SelectTrigger>
                  <SelectContent>
                    {atomosTemplates.length > 0 ? (
                      atomosTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          <div>
                            <div className="font-medium">{template.name}</div>
                            <div className="text-xs text-gray-500">
                              {template.categoryName ?? ''} • {template.status}
                            </div>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-2 py-2 flex flex-col gap-2">
                        <span className="text-sm text-gray-500">
                          {loadingAtomosTemplates ? 'Carregando...' : 'Nenhum template disponível'}
                        </span>
                        {!loadingAtomosTemplates && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-fit"
                            onClick={(e) => { e.preventDefault(); loadAtomosTemplates(); }}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Carregar templates
                          </Button>
                        )}
                      </div>
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <Select
                  value={formData.template_name}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, template_name: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o template" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(templates) && templates.length > 0 ? (
                      templates.map((template) => (
                        <SelectItem key={template.id} value={template.name}>
                          <div>
                            <div className="font-medium">{template.name}</div>
                            <div className="text-xs text-gray-500">
                              {template.category} • {template.language} • {template.status}
                            </div>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-2 py-1 text-sm text-gray-500">
                        Nenhum template disponível
                      </div>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
            )}

            {formData.connection_id == null && atomosChatConfig && (
              <div className="mt-4">
                <Label htmlFor="atomos_department_id">Equipe (opcional)</Label>
                <Select
                  value={formData.atomos_department_id ?? ''}
                  onValueChange={(value) => {
                    if (value === '' || value === '__none__') {
                      setFormData(prev => ({
                        ...prev,
                        atomos_department_id: null,
                        atomos_department_name: null,
                      }));
                      return;
                    }
                    const dep = atomosDepartments.find(d => d.id === value);
                    if (dep) {
                      setFormData(prev => ({
                        ...prev,
                        atomos_department_id: dep.id,
                        atomos_department_name: dep.name,
                      }));
                    }
                  }}
                  disabled={loadingAtomosDepartments}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={loadingAtomosDepartments ? 'Carregando equipes...' : '— Nenhuma (fluxo padrão) —'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Nenhuma (fluxo padrão) —</SelectItem>
                    {atomosDepartments.map((dep) => (
                      <SelectItem key={dep.id} value={dep.id}>
                        {dep.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.atomos_department_id
                  && !loadingAtomosDepartments
                  && atomosDepartments.length > 0
                  && !atomosDepartments.some(d => d.id === formData.atomos_department_id) && (
                    <p className="text-xs text-red-600 mt-1">
                      Equipe "{formData.atomos_department_name ?? formData.atomos_department_id}" não encontrada no ÁtomosChat. Selecione outra antes de salvar.
                    </p>
                  )}
                <p className="text-xs text-gray-500 mt-1">
                  Quando preenchida, o disparo é roteado direto para essa equipe no ÁtomosChat.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {/* Tipo de Estratégia */}
              <div>
                <Label htmlFor="strategy_type">Tipo de Estratégia *</Label>
                <Select
                  value={formData.strategy_type}
                  onValueChange={handleStrategyTypeChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {STRATEGY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          <div>
                            <div className="font-medium">{type.label}</div>
                            <div className="text-xs text-gray-500">{type.description}</div>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tag */}
              <div>
                <Label htmlFor="tag_code">Tag *</Label>
                <Select
                  value={formData.tag_code}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, tag_code: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a tag" />
                  </SelectTrigger>
                  <SelectContent>
                    {getTagOptions(formData.strategy_type || 'LEMBRETE').map((tag) => (
                      <SelectItem key={tag.value} value={tag.value}>
                        <div>
                          <div className="font-medium">{tag.label}</div>
                          <div className="text-xs text-gray-500">{tag.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Campos de Dias - Para LEMBRETE, COBRANÇA e REVISTORIA */}
            <div className="grid grid-cols-2 gap-4">
              {formData.strategy_type === 'LEMBRETE' ? (
                <div className="space-y-2">
                  <Label>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      Período (dias antes do vencimento) *
                    </div>
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label htmlFor="days_before_due_from" className="text-xs text-gray-500">De</Label>
                      <Input
                        id="days_before_due_from"
                        type="number"
                        min="0"
                        max="365"
                        placeholder="Ex: 3"
                        value={formData.days_before_due_from ?? ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          days_before_due_from: parseOptionalInt(e.target.value),
                          days_after_due: undefined,
                          days_after_due_from: undefined,
                          days_after_due_to: undefined
                        }))}
                      />
                    </div>
                    <span className="pt-6">a</span>
                    <div className="flex-1">
                      <Label htmlFor="days_before_due_to" className="text-xs text-gray-500">Até</Label>
                      <Input
                        id="days_before_due_to"
                        type="number"
                        min="0"
                        max="365"
                        placeholder="Ex: 7"
                        value={formData.days_before_due_to ?? ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          days_before_due_to: parseOptionalInt(e.target.value),
                          days_after_due: undefined,
                          days_after_due_from: undefined,
                          days_after_due_to: undefined
                        }))}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Entre X e Y dias antes do vencimento (0 = no dia do vencimento). Ex.: entre 3 e 7 dias antes
                  </p>
                </div>
              ) : (formData.strategy_type === 'COBRANCA' || formData.strategy_type === 'REVISTORIA' || formData.strategy_type === 'REATIVACAO') ? (
                <div className="space-y-2">
                  <Label>
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-orange-600" />
                      Período (dias após o vencimento) *
                    </div>
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label htmlFor="days_after_due_from" className="text-xs text-gray-500">De</Label>
                      <Input
                        id="days_after_due_from"
                        type="number"
                        min="0"
                        max="365"
                        placeholder="Ex: 1"
                        value={formData.days_after_due_from ?? ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          days_after_due_from: parseOptionalInt(e.target.value),
                          days_before_due: undefined
                        }))}
                      />
                    </div>
                    <span className="pt-6">a</span>
                    <div className="flex-1">
                      <Label htmlFor="days_after_due_to" className="text-xs text-gray-500">Até</Label>
                      <Input
                        id="days_after_due_to"
                        type="number"
                        min="0"
                        max="365"
                        placeholder="Ex: 5"
                        value={formData.days_after_due_to ?? ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          days_after_due_to: parseOptionalInt(e.target.value),
                          days_before_due: undefined
                        }))}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.strategy_type === 'COBRANCA' 
                      ? 'Se o boleto estiver entre X e Y dias vencido, use esta estratégia'
                      : formData.strategy_type === 'REVISTORIA'
                      ? 'Se estiver entre X e Y dias vencido, use esta revistoria'
                      : 'Período opcional para reativação (entre X e Y dias vencido)'
                    }
                  </p>
                </div>
              ) : null}
            </div>


            {/* Descrição */}
            <div>
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                placeholder="Descrição da estratégia (opcional)"
                value={formData.description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            {/* Template de email (visível apenas quando o canal de email está habilitado) */}
            {client?.email_canal_enabled && (
              <div className="space-y-2">
                <Label>Template de email (opcional)</Label>
                <Select
                  value={formData.email_template_id ? String(formData.email_template_id) : 'none'}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      email_template_id: value === 'none' ? null : Number(value),
                      ...(value === 'none' ? { auto_enviar_email: false } : {}),
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sem email para esta estratégia" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem email para esta estratégia</SelectItem>
                    {emailTemplates.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>{t.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Quando selecionado, o EmailEspelhamentoService usará este template ao espelhar os boletos desta estratégia por email.
                </p>
              </div>
            )}

            {client?.email_canal_enabled && formData.email_template_id && (
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="pr-4">
                  <Label className="font-medium">Enviar também por email</Label>
                  <p className="text-xs text-gray-500">
                    Ao concluir o lote desta automação, os emails são gerados e enviados automaticamente.
                  </p>
                </div>
                <Switch
                  checked={!!formData.auto_enviar_email}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, auto_enviar_email: checked }))
                  }
                />
              </div>
            )}

            {/* Status */}
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
              <Label>Estratégia ativa</Label>
            </div>

            {/* Configurações específicas de Meta API - ESCONDER para conexões extra_oficial ou quando usa fluxo personalizado */}
            {!formData.use_message_flow && selectedConnection && selectedConnection.connection_type !== 'extra_oficial' && (
              <>
            {/* Use Common Template - Apenas para LEMBRETE e COBRANCA */}
            {(formData.strategy_type === 'LEMBRETE' || formData.strategy_type === 'COBRANCA') && (
              <div className="flex items-start gap-2 p-3 border rounded-lg bg-blue-50">
                <Switch
                  checked={formData.use_common_template || false}
                  onCheckedChange={(checked) => setFormData(prev => ({ 
                    ...prev, 
                    use_common_template: checked,
                    // Resetar campos de header quando desativar template comum
                    header_enabled: checked ? prev.header_enabled : false,
                    header_type: checked ? prev.header_type : null,
                    header_url: checked ? prev.header_url : null
                  }))}
                />
                <div className="flex-1">
                  <Label htmlFor="use_common_template" className="cursor-pointer">
                    <div className="font-medium text-sm">Usar template comum (sem PIX/PDF)</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Quando marcado, o template será enviado sem PIX/PDF, mesmo para lembretes e cobranças. 
                      Útil para templates que não precisam de informações de pagamento.
                    </div>
                  </Label>
                </div>
              </div>
            )}

            {/* Enviar header document (PDF) no template especial - Apenas quando use_common_template = false */}
            {(formData.strategy_type === 'LEMBRETE' || formData.strategy_type === 'COBRANCA') && !formData.use_common_template && (
              <div className="flex items-start gap-2 p-3 border rounded-lg bg-amber-50">
                <Switch
                  checked={formData.document_header_in_template !== false}
                  onCheckedChange={(checked) => setFormData(prev => ({
                    ...prev,
                    document_header_in_template: checked
                  }))}
                />
                <div className="flex-1">
                  <Label htmlFor="document_header_in_template" className="cursor-pointer">
                    <div className="font-medium text-sm">Enviar header document (PDF do boleto)?</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Quando desmarcado, o template será enviado apenas com body e order_details, sem o PDF do boleto no cabeçalho.
                      Use quando o template na Meta não possui header.
                    </div>
                  </Label>
                </div>
              </div>
            )}

            {/* Enviar link do PDF completo (espelhado) - template especial, quando o header document está ativo */}
            {(formData.strategy_type === 'LEMBRETE' || formData.strategy_type === 'COBRANCA') && !formData.use_common_template && formData.document_header_in_template !== false && (
              <div className="flex items-start gap-2 p-3 border rounded-lg bg-violet-50">
                <Switch
                  checked={formData.enviar_link_pdf_completo || false}
                  onCheckedChange={(checked) => setFormData(prev => ({
                    ...prev,
                    enviar_link_pdf_completo: checked
                  }))}
                />
                <div className="flex-1">
                  <Label className="cursor-pointer">
                    <div className="font-medium text-sm">Enviar link do PDF completo</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Ligado: o sistema hospeda o PDF e envia um link direto (evita o anexo chegar como HTML).
                      Desligado: envia o link curto, como hoje. Requer o recurso habilitado globalmente.
                    </div>
                  </Label>
                </div>
              </div>
            )}

            {/* Template comum: opção de PDF do boleto (link dinâmico) ou header de mídia */}
            {formData.use_common_template && (
              <div className="space-y-3 p-3 border rounded-lg bg-gray-50">
                <div className="flex items-start gap-2 p-3 border rounded-lg bg-emerald-50">
                  <Switch
                    checked={formData.common_template_document_header || false}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      common_template_document_header: checked
                    }))}
                  />
                  <div className="flex-1">
                    <Label className="cursor-pointer">
                      <div className="font-medium text-sm">Incluir PDF do boleto no header (link dinâmico)</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Quando marcado, o template comum envia o PDF do boleto no cabeçalho usando o link da tabela disparos_boletos (ex.: link_boleto), um PDF por contato.
                      </div>
                    </Label>
                  </div>
                </div>
                {/* Enviar link do PDF completo (espelhado) - template comum, quando "Incluir PDF" está ligado */}
                {formData.common_template_document_header === true && (
                  <div className="flex items-start gap-2 p-3 border rounded-lg bg-violet-50">
                    <Switch
                      checked={formData.enviar_link_pdf_completo || false}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        enviar_link_pdf_completo: checked
                      }))}
                    />
                    <div className="flex-1">
                      <Label className="cursor-pointer">
                        <div className="font-medium text-sm">Enviar link do PDF completo</div>
                        <div className="text-xs text-gray-600 mt-1">
                          Ligado: o sistema hospeda o PDF e envia um link direto (evita o anexo chegar como HTML).
                          Desligado: envia o link curto, como hoje. Requer o recurso habilitado globalmente.
                        </div>
                      </Label>
                    </div>
                  </div>
                )}
                {/* Bloco destacado: link de vídeo ou imagem no header */}
                <div className="flex items-start gap-2 p-3 border rounded-lg bg-sky-50 border-sky-200">
                  <Switch
                    checked={formData.header_enabled || false}
                    onCheckedChange={(checked) => setFormData(prev => ({ 
                      ...prev, 
                      header_enabled: checked,
                      header_type: checked ? prev.header_type : null,
                      header_url: checked ? prev.header_url : null
                    }))}
                  />
                  <div className="flex-1">
                    <Label className="cursor-pointer">
                      <div className="font-medium text-sm">Enviar header de mídia (imagem ou vídeo – link fixo)</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Ative e informe abaixo o tipo e a <strong>URL do arquivo</strong> (vídeo ou imagem) para aparecer no cabeçalho do template.
                      </div>
                    </Label>
                  </div>
                </div>
                
                {formData.header_enabled && (
                  <div className="space-y-3 pl-8 border-l-2 border-sky-300 ml-2 p-3 rounded-r bg-sky-50/50">
                    <div>
                      <Label htmlFor="header_type" className="text-sm">Tipo do header</Label>
                      <Select
                        value={formData.header_type || ''}
                        onValueChange={(value) => setFormData(prev => ({ 
                          ...prev, 
                          header_type: value as 'image' | 'video' 
                        }))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="image">🖼️ Imagem</SelectItem>
                          <SelectItem value="video">🎬 Vídeo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="header_url" className="text-sm">Link do arquivo (URL da mídia)</Label>
                      <Input
                        id="header_url"
                        placeholder={formData.header_type === 'video' ? 'https://exemplo.com/video.mp4' : 'https://exemplo.com/imagem.jpg'}
                        value={formData.header_url || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          header_url: e.target.value 
                        }))}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        URL pública do vídeo ou da imagem (deve ser acessível pela Meta/WhatsApp)
                      </p>
                    </div>

                    {/* Validação visual */}
                    {formData.header_enabled && (!formData.header_type || !formData.header_url) && (
                      <Alert className="bg-yellow-50 border-yellow-200">
                        <AlertCircle className="h-4 w-4 text-yellow-600" />
                        <AlertDescription className="text-yellow-700 text-sm">
                          Selecione o tipo e insira a URL da mídia para ativar o header.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}
              </div>
            )}
            </>
            )}

            {/* Botões – habilitar quando tiver conexão (ou V2) + template ou fluxo; extra_oficial usa message_flow_id */}
            {(() => {
              const isExtraFlow = !!(formData.use_message_flow || selectedConnection?.connection_type === 'extra_oficial');
              const missingTemplateOrFlow = isExtraFlow ? !formData.message_flow_id : !formData.template_name;
              const missingConnection = formData.connection_id == null ? !formData.atomos_template_id : !formData.connection_id;
              const missingDays = formData.strategy_type === 'LEMBRETE'
                ? !((formData.days_before_due_from != null && formData.days_before_due_to != null) || (formData.days_before_due != null && formData.days_before_due >= 0))
                : (formData.strategy_type === 'COBRANCA' || formData.strategy_type === 'REVISTORIA')
                  ? !((formData.days_after_due_from != null && formData.days_after_due_to != null) || formData.days_after_due != null)
                  : false;
              const canSave = !saving && !missingTemplateOrFlow && !missingConnection && !missingDays && !!formData.strategy_type && !!formData.tag_code;
              return (
            <div className="flex gap-2">
              <Button
                onClick={handleSubmit}
                disabled={!canSave}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvando...' : editingStrategy ? 'Atualizar' : 'Salvar'}
              </Button>
              
              <Button variant="outline" onClick={handleCancelForm}>
                Cancelar
              </Button>
            </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Lista de estratégias */}
      <div className="space-y-4">
        {strategies.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-8">
              <Target className="w-12 h-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhuma estratégia configurada
              </h3>
              <p className="text-gray-600 text-center mb-4">
                Configure estratégias para definir quais templates usar em cada situação de envio
              </p>
              <Button
                onClick={() => setShowForm(true)}
                className="bg-purple-600 hover:bg-purple-700"
                disabled={connections.length === 0 && atomosChatConfig?.envioVersion !== 'v2'}
              >
                <Plus className="w-4 h-4 mr-2" />
                Configurar Primeira Estratégia
              </Button>
            </CardContent>
          </Card>
        ) : (
          strategies.map((strategy) => {
            const strategyInfo = getStrategyInfo(strategy.strategy_type);
            return (
              <div key={strategy.id}>
                <Card className={strategy.is_active ? 'border-green-200' : 'border-gray-200'}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <strategyInfo.icon className="w-5 h-5 text-purple-600" />
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{strategy.template_name}</h4>
                            <Badge variant={strategy.is_active ? 'default' : 'secondary'}>
                              {strategy.is_active ? 'Ativo' : 'Inativo'}
                            </Badge>
                            <Badge variant="outline">
                              {strategyInfo.label}
                            </Badge>
                            <Badge variant="outline" className={`bg-${strategyInfo.color}-50 text-${strategyInfo.color}-700`}>
                              Tag: {strategy.tag_code}
                            </Badge>
                          </div>
                          
                          <div className="text-sm text-gray-600">
                              <div className="flex items-center gap-2">
                                <Smartphone className="w-3 h-3" />
                                <span>{strategy.connection_id == null ? 'ÁtomosChat (Envios V2)' : (strategy.connection?.name || 'Conexão não definida')}</span>
                                {strategy.connection_id != null && strategy.connection && (
                                  <Badge 
                                    variant={strategy.connection.connection_type === 'oficial' ? 'default' : 'secondary'}
                                    className="text-xs"
                                  >
                                    {strategy.connection.connection_type === 'oficial' ? 'Meta API' : 'Extra Oficial'}
                                  </Badge>
                                )}
                                {strategy.connection_id == null && (
                                  <>
                                    <Badge variant="secondary" className="text-xs">Envios V2</Badge>
                                    {(() => {
                                      const cfgId = (strategy as any).atomos_chat_config_id;
                                      const cfg = cfgId != null ? atomosChatConfigs.find((c) => Number(c.id) === Number(cfgId)) : null;
                                      const label = cfg ? `${cfg.account_name}${cfg.channel_phone ? ` — ${cfg.channel_phone}` : ''}` : null;
                                      return label ? (
                                        <Badge variant="outline" className="text-xs">{label}</Badge>
                                      ) : null;
                                    })()}
                                  </>
                                )}
                                {strategy.tag_description && (
                                  <>
                                    <span>•</span>
                                    <span>{strategy.tag_description}</span>
                                  </>
                                )}
                              </div>
                              
                              {/* Exibição dos campos de dias */}
                              <div className="flex items-center gap-2 mt-1">
                                {strategy.strategy_type === 'LEMBRETE' && (strategy.days_before_due_from != null || strategy.days_before_due != null) && (
                                  <div className="flex items-center gap-1 text-blue-600">
                                    <Clock className="w-3 h-3" />
                                    <span>
                                      {strategy.days_before_due_from != null && strategy.days_before_due_to != null
                                        ? strategy.days_before_due_from === strategy.days_before_due_to
                                          ? (strategy.days_before_due_from === 0 ? 'no dia do vencimento' : `${strategy.days_before_due_from} dias antes`)
                                          : (strategy.days_before_due_from === 0 && strategy.days_before_due_to === 0
                                            ? 'no dia do vencimento'
                                            : `${strategy.days_before_due_from} a ${strategy.days_before_due_to} dias antes`)
                                        : (strategy.days_before_due === 0 ? 'no dia do vencimento' : `${strategy.days_before_due ?? 0} dias antes`)
                                      }
                                    </span>
                                  </div>
                                )}
                                {(strategy.strategy_type === 'COBRANCA' || strategy.strategy_type === 'REVISTORIA' || strategy.strategy_type === 'REATIVACAO') && (strategy.days_after_due_from != null || strategy.days_after_due != null) && (
                                  <div className="flex items-center gap-1 text-orange-600">
                                    <AlertCircle className="w-3 h-3" />
                                    <span>
                                      {strategy.days_after_due_from != null && strategy.days_after_due_to != null
                                        ? strategy.days_after_due_from === strategy.days_after_due_to
                                          ? `${strategy.days_after_due_from} dias depois`
                                          : `${strategy.days_after_due_from} a ${strategy.days_after_due_to} dias depois`
                                        : `${strategy.days_after_due ?? 0} dias depois`
                                      }
                                    </span>
                                  </div>
                                )}
                              </div>
                              
                              {strategy.description && (
                                <p className="mt-1 text-gray-500">{strategy.description}</p>
                              )}
                            </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(strategy)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(strategy.id!)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {editingStrategy && editingStrategy.id === strategy.id && (
                <Card className="border-purple-200 mt-4">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Edit className="w-5 h-5" />
                      Editar Estratégia
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Seleção de Conexão */}
                    <div>
                      <Label htmlFor="connection_id">Conexão WhatsApp *</Label>
                      <Select
                        value={formData.connection_id == null && atomosChatConfig ? CONNECTION_VALUE_ATOMOS_V2 : (formData.connection_id?.toString() ?? '')}
                        onValueChange={handleConnectionChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a conexão" />
                        </SelectTrigger>
                        <SelectContent>
                          {atomosChatConfig?.envioVersion === 'v2' && (
                            <SelectItem value={CONNECTION_VALUE_ATOMOS_V2}>
                              <div className="flex items-center gap-2">
                                <Wifi className="w-4 h-4" />
                                <span>ÁtomosChat (Envios V2)</span>
                              </div>
                            </SelectItem>
                          )}
                          {connections.map((connection) => (
                            <SelectItem
                              key={connection.id}
                              value={connection.id.toString()}
                              disabled={connection.connection_type !== 'extra_oficial' && !canUseTemplates(connection)}
                              title={connection.connection_type === 'oficial' && (connection.template_count || 0) === 0 ? 'Configure os templates na aba Conexões WhatsApp primeiro' : undefined}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${
                                  connection.is_active ? 'bg-green-500' : 'bg-red-500'
                                }`} />
                                <span>{connection.name}</span>
                                <span className="text-xs text-gray-500">
                                  ({connection.template_count || 0} templates)
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Seletor de conexão V2 (quando >1 config V2 do cliente) */}
                    {isV2Strategy && activeAtomosChatConfigs.length > 1 && (
                      <div>
                        <Label htmlFor="atomos_chat_config_id_edit">Conexão V2 *</Label>
                        <Select
                          value={(formData as any).atomos_chat_config_id ? String((formData as any).atomos_chat_config_id) : ''}
                          onValueChange={(v) => {
                            const id = v ? Number(v) : null;
                            setFormData(prev => ({ ...(prev as any), atomos_chat_config_id: id } as any));
                            if (id) {
                              loadAtomosTemplates(id);
                              loadAtomosDepartments(id);
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a conexão V2" />
                          </SelectTrigger>
                          <SelectContent>
                            {atomosChatConfigs.map((cfg) => (
                              <SelectItem key={cfg.id} value={String(cfg.id)}>
                                {cfg.account_name}{cfg.channel_phone ? ` — ${cfg.channel_phone}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 mt-1">
                          Esta estratégia enviará pela conexão V2 escolhida.
                        </p>
                      </div>
                    )}

                    {/* Equipe (ÁtomosChat V2): roteia o disparo para a equipe escolhida.
                        Espelha o bloco do formulário de criação — sem ele, não há como
                        atribuir/alterar a equipe ao EDITAR uma estratégia V2. */}
                    {formData.connection_id == null && atomosChatConfig && (
                      <div className="mt-4">
                        <Label htmlFor="atomos_department_id_edit">Equipe (opcional)</Label>
                        <Select
                          value={formData.atomos_department_id ?? ''}
                          onValueChange={(value) => {
                            if (value === '' || value === '__none__') {
                              setFormData(prev => ({
                                ...prev,
                                atomos_department_id: null,
                                atomos_department_name: null,
                              }));
                              return;
                            }
                            const dep = atomosDepartments.find(d => d.id === value);
                            if (dep) {
                              setFormData(prev => ({
                                ...prev,
                                atomos_department_id: dep.id,
                                atomos_department_name: dep.name,
                              }));
                            }
                          }}
                          disabled={loadingAtomosDepartments}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={loadingAtomosDepartments ? 'Carregando equipes...' : '— Nenhuma (fluxo padrão) —'} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Nenhuma (fluxo padrão) —</SelectItem>
                            {atomosDepartments.map((dep) => (
                              <SelectItem key={dep.id} value={dep.id}>
                                {dep.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {formData.atomos_department_id
                          && !loadingAtomosDepartments
                          && atomosDepartments.length > 0
                          && !atomosDepartments.some(d => d.id === formData.atomos_department_id) && (
                            <p className="text-xs text-red-600 mt-1">
                              Equipe "{formData.atomos_department_name ?? formData.atomos_department_id}" não encontrada no ÁtomosChat. Selecione outra antes de salvar.
                            </p>
                          )}
                        <p className="text-xs text-gray-500 mt-1">
                          Quando preenchida, o disparo é roteado direto para essa equipe no ÁtomosChat.
                        </p>
                      </div>
                    )}

                    {/* Tipo de Envio (para conexões extra_oficial) */}
                    {selectedConnection && selectedConnection.connection_type === 'extra_oficial' && (
                      <Card className="border-purple-200 bg-purple-50">
                        <CardContent className="pt-4">
                          <div className="space-y-4">
                            <div className="flex items-start gap-3">
                              <div className="flex-1">
                                <Label className="text-base font-medium">Fluxo de Mensagens Personalizado</Label>
                                <p className="text-sm text-gray-600 mt-1">
                                  Para API Extra Oficial, selecione um fluxo de mensagens configurado
                                </p>
                              </div>
                            </div>

                            <div>
                              <Label>Selecionar Fluxo de Mensagens *</Label>
                              <Select
                                value={formData.message_flow_id?.toString() ?? ''}
                                onValueChange={(value) => {
                                  setFormData(prev => ({ ...prev, message_flow_id: parseInt(value) }));
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione um fluxo" />
                                </SelectTrigger>
                                <SelectContent>
                                  {messageFlows.length > 0 ? (
                                    messageFlows
                                      .filter(flow => flow.connection_id === selectedConnection.id && flow.is_active)
                                      .map(flow => (
                                        <SelectItem key={flow.id} value={flow.id!.toString()}>
                                          <div>
                                            <div className="font-medium">{flow.name}</div>
                                            {flow.description && (
                                              <div className="text-xs text-gray-500">{flow.description}</div>
                                            )}
                                          </div>
                                        </SelectItem>
                                      ))
                                  ) : (
                                    <div className="px-2 py-2 text-sm text-gray-500">
                                      Nenhum fluxo disponível. Crie um na aba "Fluxos de Envio".
                                    </div>
                                  )}
                                </SelectContent>
                              </Select>
                              <Alert className="mt-3">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription className="text-xs">
                                  Configure fluxos de mensagens na aba <strong>"Fluxos de Envio"</strong>.
                                  As mensagens e delays são definidos no fluxo selecionado.
                                </AlertDescription>
                              </Alert>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Tipo de Estratégia */}
                    <div>
                      <Label htmlFor="strategy_type">Tipo de Estratégia *</Label>
                      <Select
                        value={formData.strategy_type}
                        onValueChange={handleStrategyTypeChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          {STRATEGY_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-2">
                                <type.icon className="w-4 h-4" />
                                <span>{type.label}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Tag */}
                    <div>
                      <Label htmlFor="tag_code">Tag *</Label>
                      <Select
                        value={formData.tag_code}
                        onValueChange={(value) => setFormData({ ...formData, tag_code: value as any })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a tag" />
                        </SelectTrigger>
                        <SelectContent>
                          {getTagOptions(formData.strategy_type).map((tag) => (
                            <SelectItem key={tag.value} value={tag.value}>
                              <div className="flex items-center gap-2">
                                <Tag className="w-4 h-4" />
                                <span>{tag.label}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Campos de dias baseados no tipo */}
                    {formData.strategy_type === 'LEMBRETE' && (
                      <div className="space-y-2">
                        <Label>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-blue-600" />
                            Período (dias antes do vencimento) *
                          </div>
                        </Label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <Label htmlFor="edit_days_before_due_from" className="text-xs text-gray-500">De</Label>
                            <Input
                              id="edit_days_before_due_from"
                              type="number"
                              min="0"
                              max="365"
                              placeholder="Ex: 3"
                              value={formData.days_before_due_from ?? ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                days_before_due_from: parseOptionalInt(e.target.value),
                                days_after_due: undefined,
                                days_after_due_from: undefined,
                                days_after_due_to: undefined
                              }))}
                            />
                          </div>
                          <span className="pt-6">a</span>
                          <div className="flex-1">
                            <Label htmlFor="edit_days_before_due_to" className="text-xs text-gray-500">Até</Label>
                            <Input
                              id="edit_days_before_due_to"
                              type="number"
                              min="0"
                              max="365"
                              placeholder="Ex: 7"
                              value={formData.days_before_due_to ?? ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                days_before_due_to: parseOptionalInt(e.target.value),
                                days_after_due: undefined,
                                days_after_due_from: undefined,
                                days_after_due_to: undefined
                              }))}
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Entre X e Y dias antes do vencimento (ex.: entre 3 e 7 dias antes)
                        </p>
                      </div>
                    )}

                    {(formData.strategy_type === 'COBRANCA' || formData.strategy_type === 'REVISTORIA' || formData.strategy_type === 'REATIVACAO') && (
                      <div className="space-y-2">
                        <Label>
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-orange-600" />
                            Período (dias após o vencimento) *
                          </div>
                        </Label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <Label htmlFor="edit_days_after_due_from" className="text-xs text-gray-500">De</Label>
                            <Input
                              id="edit_days_after_due_from"
                              type="number"
                              min="0"
                              max="365"
                              placeholder="Ex: 1"
                              value={formData.days_after_due_from ?? ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, days_after_due_from: parseOptionalInt(e.target.value), days_before_due: undefined }))}
                            />
                          </div>
                          <span className="pt-6">a</span>
                          <div className="flex-1">
                            <Label htmlFor="edit_days_after_due_to" className="text-xs text-gray-500">Até</Label>
                            <Input
                              id="edit_days_after_due_to"
                              type="number"
                              min="0"
                              max="365"
                              placeholder="Ex: 5"
                              value={formData.days_after_due_to ?? ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, days_after_due_to: parseOptionalInt(e.target.value), days_before_due: undefined }))}
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Se o boleto estiver entre X e Y dias vencido, use esta estratégia
                        </p>
                      </div>
                    )}

                    {/* Descrição */}
                    <div>
                      <Label htmlFor="description">Descrição</Label>
                      <Textarea
                        value={formData.description || ''}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Descrição da estratégia (opcional)"
                        rows={3}
                      />
                    </div>

                    {/* Template de email (visível apenas quando o canal de email está habilitado) */}
                    {client?.email_canal_enabled && (
                      <div className="space-y-2">
                        <Label>Template de email (opcional)</Label>
                        <Select
                          value={formData.email_template_id ? String(formData.email_template_id) : 'none'}
                          onValueChange={(value) =>
                            setFormData((prev) => ({
                              ...prev,
                              email_template_id: value === 'none' ? null : Number(value),
                              ...(value === 'none' ? { auto_enviar_email: false } : {}),
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sem email para esta estratégia" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sem email para esta estratégia</SelectItem>
                            {emailTemplates.map((t) => (
                              <SelectItem key={t.id} value={String(t.id)}>{t.nome}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">
                          Quando selecionado, o EmailEspelhamentoService usará este template ao espelhar os boletos desta estratégia por email.
                        </p>
                      </div>
                    )}

                    {client?.email_canal_enabled && formData.email_template_id && (
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div className="pr-4">
                          <Label className="font-medium">Enviar também por email</Label>
                          <p className="text-xs text-gray-500">
                            Ao concluir o lote desta automação, os emails são gerados e enviados automaticamente.
                          </p>
                        </div>
                        <Switch
                          checked={!!formData.auto_enviar_email}
                          onCheckedChange={(checked) =>
                            setFormData((prev) => ({ ...prev, auto_enviar_email: checked }))
                          }
                        />
                      </div>
                    )}

                    {/* Status ativo */}
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="is_active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      />
                      <Label htmlFor="is_active">Estratégia ativa</Label>
                    </div>

                    {/* Botões */}
                    <div className="flex justify-end gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowForm(false);
                          setEditingStrategy(null);
                          setFormData({
                            strategy_type: 'LEMBRETE',
                            tag_code: 'L1',
                            days_before_due: undefined,
                            days_before_due_from: 3,
                            days_before_due_to: 7,
                            days_after_due: undefined,
                            days_after_due_from: undefined,
                            days_after_due_to: undefined,
                            is_active: true
                          });
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleSubmit}
                        disabled={saving}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {saving ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Salvando...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Salvar Regras
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TemplateStrategiesTab;